import { CloseButton } from "@chakra-ui/react";
import React, { useState, useRef, useEffect } from "react";

interface WrittenFeedbackProps {
  onSubmit: (feedback: string) => void;
  onCancel?: () => void;
  onClose?: () => void;
  initialText?: string;
  placeholderText?: string;
  title?: string;
  submitButtonText?: string;
  showThankYou?: boolean;
  thankYouMessage?: string;
  autoFocus?: boolean; // Optional prop to control autofocus behavior
}

export const WrittenFeedbackForm: React.FC<WrittenFeedbackProps> = ({
  onSubmit,
  onCancel,
  initialText = "",
  placeholderText = "Your feedback helps us improve...",
  title = "What feedback would you like to provide?",
  submitButtonText = "Submit",
  autoFocus = true, // Default to true
}) => {
  const [feedback, setFeedback] = useState(initialText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus the textarea when the component mounts
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      // Short timeout to ensure the modal transition is complete
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [autoFocus]);

  const handleSubmit = () => {
    if (feedback.trim()) {
      onSubmit(feedback);
    }
  };

  return (
    <div className="flex flex-col w-full">
      <p className="mb-4 text-left font-medium">{title}</p>
      <textarea
        ref={textareaRef}
        className="w-full p-3 border border-[var(--color-grey-light)] rounded-md mb-4 hover:border-[var(--color-blue)] focus:border-[var(--color-blue)] focus:outline-[var(--color-blue)]"
        rows={4}
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder={placeholderText}
      />
      <div className="flex justify-end space-x-3">
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-[var(--color-grey-light)] rounded-md text-sm"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSubmit}
          className="px-5 py-2 bg-[var(--color-blue)] text-white rounded-xl text-sm"
        >
          {submitButtonText}
        </button>
      </div>
    </div>
  );
};

// Thank you message component
const ThankYouMessage: React.FC<{
  message?: string;
  onClose?: () => void;
}> = ({ message = "Thank you for your feedback!", onClose }) => {
  return (
    <div className="flex flex-col items-center w-full py-4">
      <p className="text-lg font-medium mb-5 text-center">{message}</p>
      {onClose && (
        <button
          onClick={onClose}
          className="px-5 py-2 bg-[var(--color-blue)] text-white rounded-md text-sm"
        >
          Close
        </button>
      )}
    </div>
  );
};

// A modal popup version that includes the backdrop
export const WrittenFeedbackPopup: React.FC<
  WrittenFeedbackProps & { isOpen: boolean }
> = ({ isOpen, showThankYou = false, thankYouMessage, onClose, ...props }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 px-4 md:px-0"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="bg-white rounded-lg p-5 w-full max-w-sm mx-auto">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-semibold">Feedback</h2>
          <CloseButton
            w={4}
            h={4}
            onClick={() =>
              showThankYou && onClose
                ? onClose()
                : props.onCancel && props.onCancel()
            }
          />
        </div>

        {showThankYou ? (
          <ThankYouMessage message={thankYouMessage} onClose={onClose} />
        ) : (
          <WrittenFeedbackForm {...props} />
        )}
      </div>
    </div>
  );
};
